import tzLookup from "@photostructure/tz-lookup"
import { findObservatoryByCode } from "../../data/mpc-observatories.js"

export type LocationPick =
  | { type: "mpc"; label: string; siteCode: string; observatoryName?: string }
  | { type: "custom"; label: string; lat: number; lon: number; elev: number }

export function normalizeLon(lon: number): number {
  // MPC data uses 0..360. Convert to -180..180 for LST math and tz lookup.
  return lon > 180 ? lon - 360 : lon
}

export function getObserverCoords(input: {
  siteCode: string
  customLat: number | null
  customLon: number | null
}): { lat: number; lon: number } | null {
  if (input.customLat !== null && input.customLon !== null) {
    return { lat: input.customLat, lon: normalizeLon(input.customLon) }
  }
  if (input.siteCode) {
    const obs = findObservatoryByCode(input.siteCode)
    if (obs) return { lat: obs.latitude, lon: normalizeLon(obs.longitude) }
  }
  return null
}

export function getCurrentLocationLabel(input: {
  useCustomCoords: boolean
  observatoryName: string | null
  siteCode: string
  customLat: number | null
  customLon: number | null
  customElev: number
  locationPicks: LocationPick[]
}): string {
  if (input.useCustomCoords && input.observatoryName === "My Location") {
    return "My Location"
  }

  const matchingPick = input.locationPicks.find(pick => {
    if (pick.type === "mpc") {
      return pick.siteCode === input.siteCode && !input.useCustomCoords
    }
    return (
      input.useCustomCoords &&
      pick.lat === input.customLat &&
      pick.lon === input.customLon &&
      pick.elev === input.customElev
    )
  })

  if (matchingPick) return matchingPick.label

  if (
    input.observatoryName &&
    input.observatoryName !== "Unknown observatory code"
  ) {
    return input.observatoryName
  }

  if (input.useCustomCoords && input.customLat !== null && input.customLon !== null) {
    return `${input.customLat}°, ${input.customLon}°`
  }

  if (input.siteCode) return input.siteCode
  return "No location selected"
}

export function isCurrentLocationSaved(input: {
  useCustomCoords: boolean
  observatoryName: string | null
  siteCode: string
  customLat: number | null
  customLon: number | null
  customElev: number
  locationPicks: LocationPick[]
}): boolean {
  if (input.useCustomCoords && input.observatoryName === "My Location") return false

  return input.locationPicks.some(pick => {
    if (pick.type === "mpc") {
      return pick.siteCode === input.siteCode && !input.useCustomCoords
    }
    return (
      input.useCustomCoords &&
      pick.lat === input.customLat &&
      pick.lon === input.customLon &&
      pick.elev === input.customElev &&
      input.observatoryName !== "My Location"
    )
  })
}

export function lookupTimezone(input: {
  siteCode: string
  customLat: number | null
  customLon: number | null
}): string | null {
  let lat: number | null = null
  let lon: number | null = null

  // Prefer current coords if set (works for MPC and custom).
  if (input.customLat !== null && input.customLon !== null) {
    lat = input.customLat
    lon = input.customLon
  } else if (input.siteCode) {
    const observatory = findObservatoryByCode(input.siteCode)
    if (observatory) {
      lat = observatory.latitude
      lon = observatory.longitude
    }
  }

  if (lat === null || lon === null) return null

  const normalizedLon = normalizeLon(lon)
  try {
    return tzLookup(lat, normalizedLon)
  } catch (error) {
    console.error("Failed to lookup timezone:", error)
    return null
  }
}


